import Card from 'components/Card/Card'
import API from 'core/api'
import { getAccessToken, getPartnerId, getUA } from 'core/singleton'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAsync } from 'react-async'
import styled from 'styled-components'
import { getLocation, requestBridge, throttle } from 'core/util/default'
import { useHistory } from 'react-router-dom'
import PageHeader from 'components/Page/PageHeader'
import ButtonBack from 'components/Button/ButtonBack'
import Loading from 'components/Loading/Loading'
import PageContent from 'components/Page/PageContent'
import { useRecoilValue } from 'recoil'
import { IconBack } from 'components/Icons'
import { decodeString } from 'core/util/search'
import { useTranslation } from 'react-i18next'
import { BRIDGE, ONLY_POST_MSG_PARTNERS } from 'core/constants/bridge'
import { STORE_CURATION } from 'core/store/curation'
import { IS_OPEN_LINK, STORE_ROOT_PROPS } from 'core/store/global'
import { getExecutorForPartner } from 'core/util/partner'
import { getUrlQueryParameter, moveToLink, parseObjectToQueryString } from 'core/util/url'
import { filterObject } from 'core/util/object'
import { GAHelper } from 'core/ga'
import { GA_EVENT } from 'core/ga/enums'
import { useLocation } from 'react-router-dom'
import { LINK_MOVE_TYPE } from 'core/constants'
import { STORE_AIRBRIDGE, STORE_MARKETING_GTM } from 'core/store/analysis'
import { BroadcastParams } from '@types'

interface PageCurationProps {
  id: string
  title: string
}

const PageCuration: React.FC<PageCurationProps> = ({ id, title: _title }) => {
  const { t } = useTranslation()
  const title = decodeString(_title)
  const curation = useRecoilValue(STORE_CURATION)
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const marketingGTM = useRecoilValue(STORE_MARKETING_GTM)
  const [nextKey, setNextKey] = useState<string>('init')
  const [broadcastItem, setBroadcastItem] = useState<CurationDetailItem[]>([])
  const [descOpen, setDescOpen] = useState<boolean>(false)
  const pageCurationRef = useRef<HTMLDivElement>(null)
  const history = useHistory()
  const root = useRecoilValue(STORE_ROOT_PROPS)
  const partnerId = getPartnerId()
  const isBridge = root?.bridge
  const query = getUrlQueryParameter()
  const location = useLocation()
  const { isSDK } = getUA()

  // 롯데면세점 고객사에서 Footer 사이즈로 인해 무한 스크롤이 안되는 현상 처리
  const isInfiniteScrollWithoutFooter = useMemo(() => {
    return partnerId === 'ldfs'
  }, [partnerId])

  const { data, isLoading, reload } = useAsync({
    promiseFn: API.getCurationDetail,
    curationId: id,
    nextKey,
    limit: 10,
  })

  /**
   * @name EventHandler
   **/
  const handleClickBack = useCallback(() => {
    new GAHelper().sendEvent(GA_EVENT.CLICK_BACK, {
      partnersId: partnerId,
      location: getLocation(location),
    })

    history.goBack()
  }, [history, location, partnerId])

  const handleClickDesc = useCallback(() => {
    setDescOpen((prev) => !prev)
  }, [])

  const handleLoadMore = throttle(
    useCallback(() => {
      const { scrollY, innerHeight, document } = window
      const footerHeight = isInfiniteScrollWithoutFooter ? 503 : 0

      if (document.body.scrollHeight - scrollY - 250 - footerHeight < innerHeight && !isLoading) {
        if (nextKey) {
          reload()
        }
      }
    }, [isInfiniteScrollWithoutFooter, isLoading, nextKey, reload]),
    1000,
  )

  const handleClickBroadcast = useCallback(
    ({
        link,
        broadcastId,
        broadcastTypeCode,
        broadcastName,
      }: {
        link?: string
        broadcastId?: string
        isBridge?: boolean
        broadcastTypeCode?: string
        broadcastName?: string
        order?: number
      }) =>
      () => {
        if (!link) return

        if (marketingGTM?.checkAvailability()) {
          const queryStringPrefix = link.indexOf('?') >= 0 ? '&' : '?'
          link = link.concat(`${queryStringPrefix}_gl=${marketingGTM.getGl()}`)
        }

        const params: BroadcastParams = {
          broadcastUrl: link,
        }
        if (ONLY_POST_MSG_PARTNERS.includes(partnerId)) {
          params.broadcastId = broadcastId
        }

        new GAHelper().sendEvent(GA_EVENT.CLICK_CONTENT, {
          partnersId: partnerId,
          location: getLocation(location),
          broadcastTypeCode: broadcastTypeCode,
          broadcastName: broadcastName,
          position: title,
        })

        if (getExecutorForPartner(partnerId)?.reqMoveBroadcastBridge?.(link)) {
          return
        }

        if (root?.bridge || window[BRIDGE.BROADCAST] || isSDK) {
          requestBridge(BRIDGE.BROADCAST, params)
        } else {
          moveToLink({
            airbridge,
            link,
            type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
          })
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [airbridge, isOpenLink, location, marketingGTM, partnerId, title, root?.bridge, isSDK],
  )

  /**
   * @name DOMElement
   **/
  const descriptionEl = useMemo(() => {
    const findCuration = curation.find((x) => x.curationId === id)
    if (!findCuration || !findCuration.description || !findCuration.imageUrl) return
    const { imageUrl, curationName, description } = findCuration
    const rootClassName = descOpen ? 'sauce_desc open' : 'sauce_desc'
    return (
      <div className={rootClassName}>
        <div>
          <img className="curation_logo" src={imageUrl + '?h=225&w=225&f=jpg'} alt="" />
          <div className="curation_name">{curationName}</div>
        </div>
        <div className="curation_desc">{description}</div>
        <div className="curation_btn">
          <button onClick={handleClickDesc}>
            {descOpen ? (
              <>
                <CurationIconWrapper>
                  <IconBack />
                </CurationIconWrapper>
                <CurationIconWrapper>{t('close')}</CurationIconWrapper>
              </>
            ) : (
              <>
                <CurationIconWrapper>
                  <IconBack />
                </CurationIconWrapper>
                <CurationIconWrapper>{t('more')}</CurationIconWrapper>
              </>
            )}
          </button>
        </div>
      </div>
    )
  }, [curation, id, descOpen, handleClickDesc, t])

  const curationList = useMemo(() => {
    const token = getAccessToken()

    return broadcastItem.map((item, i) => {
      const {
        broadcastId,
        broadcastName: title,
        broadcastStateCode,
        broadcastThumbnailList,
        outputPath,
        partnerProfileImageUrl: shopLogo,
        previewUrl,
        room,
        shopName,
        totalDuration: playTime,
        usePurchaseEvent: isShowPurchaseAuthBadge,
      } = item

      const incomingCounterInfo = room?.incomingCounterInfo

      const representImage = broadcastThumbnailList?.filter((x) => x.isRepresentative === true)

      const queryString = parseObjectToQueryString({
        accessToken: token,
        npc: id,
        ...filterObject(query, (k) => Boolean(k.startsWith('utm'))),
      })
      const link = outputPath + queryString
      const isLive = broadcastStateCode === 'onair'

      return (
        <Card
          small
          key={broadcastId}
          broadcastId={broadcastId}
          broadcastStateCode={broadcastStateCode}
          link={link}
          isOpenLink={isOpenLink}
          stream={previewUrl ?? undefined}
          header={{ isLive, playTime, incomingCounterInfo }}
          bottom={{ isShowPurchaseAuthBadge }}
          thumbnail={representImage[0].thumbnailUrl}
          info={{ shopName, title, shopLogo }}
          isBridge={!!isBridge}
          root={root}
          order={i}
          handleClickBroadcast={handleClickBroadcast}
        />
      )
    })
  }, [broadcastItem, id, isOpenLink, isBridge, root, handleClickBroadcast, query])

  const info = useMemo(() => {
    if (data && data.totalCount) {
      return t('videos', { count: data.totalCount })
    }
  }, [data, t])

  /**
   * @name LifeCycleHook
   **/

  useEffect(() => {
    const { innerHeight } = window
    if (pageCurationRef.current) {
      if (nextKey && innerHeight > pageCurationRef.current.clientHeight) {
        reload()
      }
    }
  }, [nextKey, reload])

  useEffect(() => {
    if (data && data.items) {
      setBroadcastItem((prev) => [...prev, ...data.items])
      const nextKey = data && data.nextKey
      setNextKey(nextKey)
    }
  }, [data])

  useEffect(() => {
    window.addEventListener('scroll', handleLoadMore)
    return () => {
      window.removeEventListener('scroll', handleLoadMore)
    }
  }, [handleLoadMore])

  const smallClassName = getPartnerId() === 'lg-uplus' ? 'small_sf' : 'small'
  return (
    <StyledPageCuration ref={pageCurationRef} smallClassName={smallClassName}>
      <PageHeader message={title} id="sf_curationAll">
        <ButtonBack onClick={handleClickBack} />
      </PageHeader>
      {descriptionEl}
      <PageContent info={info} className="curation_list">
        {curationList}
      </PageContent>
      {nextKey && <Loading isLoading />}
    </StyledPageCuration>
  )
}

const StyledPageCuration = styled.div<{ smallClassName: 'small' | 'small_sf' }>`
  .curation_list {
    > .${(props) => props.smallClassName} {
      padding-left: 16px;
      margin-bottom: 40px;
      display: flex;
      flex-direction: column;
      align-items: center;
      .card_wrapper {
        width: 100%;
        height: unset;
        padding-top: 134%;
      }
      .card_info {
        width: 100%;
      }
    }

    @media (max-width: 517px) {
      > div {
        width: 50%;
      }
    }
    @media (min-width: 518px) and (max-width: 768px) {
      > div {
        width: 33.3%;
      }
    }
    @media (min-width: 769px) and (max-width: 980px) {
      > div {
        width: 25%;
      }
    }
    @media (min-width: 981px) and (max-width: 1200px) {
      > div {
        width: 20%;
      }
    }
    @media (min-width: 1201px) {
      > div {
        width: 16.66%;
      }
    }
  }
  .sauce_desc {
    background: ${(props) => props.theme.curationBackground};
    > div:first-of-type {
      display: flex;
      /* justify-content: center; */
      align-items: center;
      padding: 15px 15px 0px 15px;
    }
    .curation_name {
      font-size: 16px;
      font-weight: 700;
      margin-left: 18px;
    }
    .curation_logo {
      width: 70px;
      height: 70px;
      border-radius: 35px;
      box-shadow: 1px 1px 1px 0 ${(props) => props.theme.boxShadow};
    }
    .curation_desc {
      margin: 10px 15px;
      font-size: 12px;
      height: 38px;
      overflow: hidden;
      color: ${(props) => props.theme.font.third};
    }
    .curation_btn {
      padding: 7px 3px;
      svg {
        display: block;
        width: 14px;
        height: 14px;
        transform: translateY(-4px) rotate(270deg);
      }
      display: flex;
      justify-content: flex-end;
    }
    button {
      cursor: pointer;
      display: flex;
      height: 21px;
      width: 62px;
      margin-right: 15px;
      margin-bottom: 15px;
      border: 1px transparent solid;
      border-radius: 21px;
      color: ${(props) => props.theme.font.primary};
      background: ${(props) => props.theme.background};
      div:first-of-type {
        /* margin-left: auto; */
      }
      div:last-of-type {
        line-height: 15px;
        font-size: 11px;
        flex: 1;
      }
    }
    &.open {
      .curation_desc {
        height: unset;
      }
      .curation_btn {
        svg {
          width: 14px;
          height: 14px;
          transform: translateY(4px) rotate(90deg);
        }
      }
    }
  }
`
const CurationIconWrapper = styled.div`
  color: ${(props) => props.theme.all};
`

export default PageCuration
